import { useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Input,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Alert,
  Typography,
} from "@altimateai/lego";
import { Stack, LoadingButton } from "@altimateai/lego";
import { toast } from "@altimateai/lego";
import { configureSSO } from "../../helpers/apis";
import { SSOProvider, isValidSSOProvider } from "../../helpers/constants";
import { CheckIcon } from "@assets/icons";
import { SettingsTracking } from "@/lib/tracking/settings";

const configureSSOSchema = z.object({
  client_id: z.string().min(1, "Client ID is required"),
  client_secret: z.string().min(1, "Client Secret is required"),
  oidc_url: z.string().url("Please enter a valid OIDC Endpoint"),
});

type ConfigureSSOFormData = z.infer<typeof configureSSOSchema>;

export const ConfigureSSO = () => {
  const { ssoClient } = useParams<{ ssoClient: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm<ConfigureSSOFormData>({
    resolver: zodResolver(configureSSOSchema),
    mode: "onChange",
    defaultValues: {
      client_id: "",
      client_secret: "",
      oidc_url: "",
    },
  });

  const { isValid } = form.formState;

  // Get formatted SSO client name
  const ssoClientDisplayName = ssoClient
    ? ssoClient.charAt(0).toUpperCase() + ssoClient.slice(1).toLowerCase()
    : "SSO";

  // Supported SSO clients
  const supportedClients = Object.values(SSOProvider);

  const onSubmit = async (data: ConfigureSSOFormData) => {
    setIsLoading(true);
    try {
      const provider =
        ssoClient && isValidSSOProvider(ssoClient.toLowerCase())
          ? ssoClient.toLowerCase()
          : SSOProvider.OKTA;
      await configureSSO(data, provider);
      SettingsTracking.ssoConfigured(provider);
      setIsSuccess(true);
      form.reset();
    } catch (error: unknown) {
      const errorMessage =
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data
          ?.message ||
        (error as { message?: string })?.message ||
        "Failed to configure SSO. Please try again.";
      toast({
        variant: "destructive",
        title: "Configuration Failed",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!ssoClient || !isValidSSOProvider(ssoClient.toLowerCase())) {
    return (
      <div className="al-p-6">
        <Typography variant="h3">Configure SSO</Typography>
        <p aria-label="Unsupported provider message">
          SSO client &quot;{ssoClient}&quot; is not supported yet. Supported clients:{" "}
          {supportedClients
            .map(client => client.charAt(0).toUpperCase() + client.slice(1))
            .join(", ")}
        </p>
      </div>
    );
  }

  return (
    <div className="al-p-6">
      <div className="al-max-w-lg al-mx-auto al-space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Configure {ssoClientDisplayName} SSO</CardTitle>
            <Typography variant="body">
              Enter your {ssoClientDisplayName} application credentials to enable SSO
              authentication.
            </Typography>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="al-space-y-4"
                aria-label="SSO configuration form"
              >
                <FormField
                  control={form.control}
                  name="client_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client ID *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={`Enter your ${ssoClientDisplayName} Client ID`}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="client_secret"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client Secret *</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder={`Enter your ${ssoClientDisplayName} Client Secret`}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="oidc_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>OIDC Endpoint *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={`Enter your ${ssoClientDisplayName} OIDC discovery endpoint URL`}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="al-pt-2">
                  <LoadingButton
                    type="submit"
                    loading={isLoading}
                    disabled={!isValid}
                    className="al-w-full"
                  >
                    Configure SSO
                  </LoadingButton>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>

        {isSuccess && (
          <Alert aria-label="Success message">
            <Stack gap="sm" align="center">
              <CheckIcon className="al-h-4 al-w-4 al-text-green-600" />
              <div>
                <strong>{ssoClientDisplayName} configuration updated successfully!</strong>
                <p className="al-text-sm">
                  Now contact Altimate administrators to complete the setup.
                </p>
              </div>
            </Stack>
          </Alert>
        )}
      </div>
    </div>
  );
};
